import {
  TWO,
  KARO_TWO,
  type CardId,
  dealHands,
  groupByRank,
  highestCards,
  lowestCards,
  rankOf,
  sortHand,
} from './cards.js';
import {
  DEFAULT_CONFIG,
  IllegalMoveError,
  PLACE_POINTS,
  SEATS,
  type Count,
  type Deal,
  type Exchange,
  type Match,
  type MatchConfig,
  type Move,
  type Seat,
} from './types.js';

/* ------------------------------------------------------------------ *
 * Yardımcılar
 * ------------------------------------------------------------------ */

export const isActive = (deal: Deal, seat: Seat): boolean =>
  deal.hands[seat]!.length > 0;

export const activeSeats = (deal: Deal): Seat[] =>
  SEATS.filter((s) => isActive(deal, s));

/** Saat yönünde bir sonraki, elinde kartı olan oyuncu. */
export function nextActive(deal: Deal, from: Seat): Seat {
  for (let i = 1; i <= 4; i++) {
    const s = ((from + i) % 4) as Seat;
    if (isActive(deal, s)) return s;
  }
  throw new Error('Aktif oyuncu kalmadı');
}

/**
 * Hedefe özel derin kopya. `structuredClone` yerine bunu kullanıyoruz:
 * bot simülasyonu bunu milyonlarca kez çağıracak ve burada kopyalanacak
 * her alan biliniyor. `history` girdileri oluşturulduktan sonra hiç
 * değiştirilmediği için sığ kopya yeterli.
 */
function cloneDeal(d: Deal): Deal {
  return {
    hands: d.hands.map((h) => [...h]),
    turn: d.turn,
    trick: {
      ...d.trick,
      plays: d.trick.plays.map((p) => ({ seat: p.seat, cards: [...p.cards] })),
    },
    finishOrder: [...d.finishOrder],
    playedByRank: [...d.playedByRank],
    history: d.history.slice(),
  };
}

function clone(m: Match): Match {
  return {
    config: { ...m.config },
    seed: m.seed,
    phase: m.phase,
    dealIndex: m.dealIndex,
    scores: [...m.scores],
    deal: m.deal ? cloneDeal(m.deal) : null,
    exchange: m.exchange
      ? {
          forced: m.exchange.forced.map((t) => ({ ...t, cards: [...t.cards] })),
          pending: m.exchange.pending.map((p) => ({ ...p })),
          chosen: m.exchange.chosen.map((t) => ({ ...t, cards: [...t.cards] })),
        }
      : null,
    lastFinishOrder: m.lastFinishOrder ? [...m.lastFinishOrder] : null,
    finishOrders: m.finishOrders.map((o) => [...o]),
    lastDeal: m.lastDeal ? cloneDeal(m.lastDeal) : null,
  };
}

/* ------------------------------------------------------------------ *
 * Geçerli hamleler
 * ------------------------------------------------------------------ */

/**
 * Sıradaki oyuncunun oynayabileceği tüm hamleler.
 *
 * UI'daki "oynanabilir kartları vurgula", "geçerli hamlen yok", "tek hamlen
 * var → otomatik oyna" özelliklerinin tamamı bu fonksiyonun çıktısı.
 *
 * Renk önemsiz olduğu için her rütbe/adet kombinasyonu için tek bir temsilci
 * kart seti üretiliyor — kullanıcıya "hangi 7'yi atayım?" diye sorulmaz.
 */
export function legalMoves(deal: Deal, seat: Seat): Move[] {
  const hand = deal.hands[seat]!;
  if (hand.length === 0) return [];

  const leading = deal.trick.count === 0;
  const moves: Move[] = [];

  for (const [rank, cards] of groupByRank(hand)) {
    if (leading) {
      // Lider istediği adette açabilir; üçlüsünü bölüp ikili de atabilir.
      for (let k = 1; k <= cards.length; k++) {
        moves.push({ type: 'play', cards: cards.slice(0, k) });
      }
    } else {
      // Takipçi: turun kart sayısına eşit ve KESİNLİKLE daha yüksek rütbe.
      // Renk sıralaması olmadığı için eşit rütbe geçemez.
      const k = deal.trick.count as Count;
      if (rank > deal.trick.rank! && cards.length >= k) {
        moves.push({ type: 'play', cards: cards.slice(0, k) });
      }
    }
  }

  moves.sort((a, b) => {
    if (a.type !== 'play' || b.type !== 'play') return 0;
    return rankOf(a.cards[0]!) - rankOf(b.cards[0]!) || a.cards.length - b.cards.length;
  });

  // Lider pas geçemez — turu açmak zorunda.
  if (!leading) moves.push({ type: 'pass' });
  return moves;
}

/** Hamle geçerli mi? Geçersizse Türkçe sebep, geçerliyse null döner. */
export function checkMove(match: Match, seat: Seat, move: Move): string | null {
  if (match.phase === 'exchange') return 'Kart değişimi tamamlanmadan oynanamaz';
  if (match.phase !== 'play' || !match.deal) return 'Maç bitti';

  const deal = match.deal;
  if (deal.turn !== seat) return 'Sıra sende değil';

  const leading = deal.trick.count === 0;

  if (move.type === 'pass') {
    return leading ? 'Turu açan oyuncu pas geçemez' : null;
  }

  const cards = move.cards;
  if (cards.length < 1 || cards.length > 4) return 'En az 1, en fazla 4 kart atılır';
  if (new Set(cards).size !== cards.length) return 'Aynı kart birden fazla kez atılamaz';

  const hand = new Set(deal.hands[seat]!);
  if (!cards.every((c) => hand.has(c))) return 'Bu kartlar elinde yok';

  const rank = rankOf(cards[0]!);
  if (!cards.every((c) => rankOf(c) === rank)) return 'Atılan kartların hepsi aynı rütbe olmalı';

  if (leading) return null;

  if (cards.length !== deal.trick.count) {
    return `Bu turda ${deal.trick.count} kart atılmalı`;
  }
  if (rank <= deal.trick.rank!) return 'Daha yüksek rütbe atmalısın';
  return null;
}

export const isLegal = (match: Match, seat: Seat, move: Move): boolean =>
  checkMove(match, seat, move) === null;

/* ------------------------------------------------------------------ *
 * Hamle uygulama
 * ------------------------------------------------------------------ */

/** Hamleyi uygular ve YENİ bir Match döner (girdi değiştirilmez). */
export function applyMove(match: Match, seat: Seat, move: Move): Match {
  const error = checkMove(match, seat, move);
  if (error) throw new IllegalMoveError(error);

  const next = clone(match);
  if (move.type === 'pass') doPass(next, seat);
  else doPlay(next, seat, move.cards);
  return next;
}

function doPlay(match: Match, seat: Seat, cards: CardId[]): void {
  const deal = match.deal!;
  const played = new Set(cards);
  deal.hands[seat] = deal.hands[seat]!.filter((c) => !played.has(c));

  const rank = rankOf(cards[0]!);
  deal.playedByRank[rank - 3]! += cards.length;
  deal.trick = {
    count: cards.length as Count,
    rank,
    holder: seat,
    passStreak: 0,
    plays: [...deal.trick.plays, { seat, cards: [...cards] }],
  };
  deal.history.push({ kind: 'play', seat, cards: [...cards] });

  // Son kartını attı → sırayı aldı, ama TURU KAZANMIŞ SAYILMAZ.
  // Diğerleri bu combo'nun üzerinden oynamaya devam eder.
  if (deal.hands[seat]!.length === 0) {
    deal.finishOrder.push(seat);
    deal.history.push({ kind: 'finish', seat, place: deal.finishOrder.length });
  }

  // 3 kişi bitti → el sona erdi, kalan oyuncu sonuncu.
  if (activeSeats(deal).length <= 1) {
    endDeal(match);
    return;
  }

  // "2" hiçbir şeyle geçilemez → pas turunu beklemeye gerek yok, tur anında kesilir.
  if (rank === TWO) {
    deal.history.push({ kind: 'cut', seat });
    endTrick(match, seat);
    return;
  }

  deal.turn = nextActive(deal, seat);
}

function doPass(match: Match, seat: Seat): void {
  const deal = match.deal!;
  deal.trick = { ...deal.trick, passStreak: deal.trick.passStreak + 1 };
  deal.history.push({ kind: 'pass', seat });

  const holder = deal.trick.holder!;
  // YUMUŞAK PAS: kimse elenmediği için turu kapatmak, turu tutan dışındaki
  // TÜM aktif oyuncuların ardışık pas geçmesini gerektirir. Turu tutan oyuncu
  // kartlarını bitirdiyse (artık aktif değil) eşik bir artar — o kişi zaten
  // sıraya girmeyeceği için.
  const threshold = activeSeats(deal).length - (isActive(deal, holder) ? 1 : 0);

  if (deal.trick.passStreak >= threshold) {
    endTrick(match, holder);
    return;
  }
  deal.turn = nextActive(deal, seat);
}

/** Turu kapatır ve liderliği devreder. */
function endTrick(match: Match, winner: Seat): void {
  const deal = match.deal!;
  // Turu kazanan kartlarını bitirdiyse liderlik saat yönünde ilk aktif oyuncuya geçer.
  const nextLeader = isActive(deal, winner) ? winner : nextActive(deal, winner);

  deal.history.push({ kind: 'trickEnd', winner, nextLeader });
  deal.trick = { count: 0, rank: null, holder: null, passStreak: 0, plays: [] };
  deal.turn = nextLeader;
}

/* ------------------------------------------------------------------ *
 * El / set / maç akışı
 * ------------------------------------------------------------------ */

function endDeal(match: Match): void {
  const deal = match.deal!;
  for (const s of SEATS) {
    if (!deal.finishOrder.includes(s)) deal.finishOrder.push(s);
  }

  const order = deal.finishOrder;
  order.forEach((s, i) => {
    match.scores[s]! += PLACE_POINTS[i]!;
  });
  match.lastFinishOrder = [...order];
  match.finishOrders.push([...order]);
  match.lastDeal = deal;
  match.dealIndex += 1;

  if (match.dealIndex >= match.config.dealsPerSet * match.config.sets) {
    match.phase = 'matchOver';
    return;
  }

  // Sonraki el dağıtılır, ardından değişim fazına girilir.
  // (Değişim yeni ellerin üzerinde yapılır — bitmiş elde kimsede kart yok.)
  startDeal(match);

  // SET BAŞLARINDA DEĞİŞİM YOK. Her set temiz sayfa açıyor: bir setin
  // sonuncusu, bir sonraki sete cezalı başlamıyor. Maçın ilk eli de bunun
  // kapsamında — öncesinde bir el olmadığı için zaten değişim olamazdı.
  if (match.dealIndex % match.config.dealsPerSet === 0) return;

  const exchange = buildExchange(match, order);
  match.exchange = exchange;
  // Değişim kapalıysa (kart sayısı 0) bekleyen seçim olmaz — doğrudan oyuna geç.
  if (exchange.pending.length > 0) match.phase = 'exchange';
}

/** Yeni el dağıtır. Lider: ilk elde karo 2 sahibi, sonrasında önceki elin sonuncusu. */
function startDeal(match: Match): void {
  const hands = dealHands(`${match.seed}#${match.dealIndex}`);
  const leader: Seat =
    match.lastFinishOrder === null
      ? (SEATS.find((s) => hands[s]!.includes(KARO_TWO)) as Seat)
      : match.lastFinishOrder[3]!;

  match.deal = {
    hands,
    turn: leader,
    trick: { count: 0, rank: null, holder: null, passStreak: 0, plays: [] },
    finishOrder: [],
    playedByRank: new Array(13).fill(0),
    history: [],
  };
  match.phase = 'play';
  match.exchange = null;
}

export function createMatch(seed: string, config: Partial<MatchConfig> = {}): Match {
  const match: Match = {
    config: { ...DEFAULT_CONFIG, ...config },
    seed,
    phase: 'play',
    dealIndex: 0,
    scores: [0, 0, 0, 0],
    deal: null,
    exchange: null,
    lastFinishOrder: null,
    finishOrders: [],
    lastDeal: null,
  };
  startDeal(match);
  return match;
}

/** Elle kurulmuş bir el — test, replay ve bulmaca senaryoları için. */
export function matchFromHands(
  hands: CardId[][],
  leader: Seat,
  config: Partial<MatchConfig> = {},
): Match {
  return {
    config: { ...DEFAULT_CONFIG, ...config },
    seed: 'manual',
    phase: 'play',
    dealIndex: 0,
    scores: [0, 0, 0, 0],
    deal: {
      hands: hands.map(sortHand),
      turn: leader,
      trick: { count: 0, rank: null, holder: null, passStreak: 0, plays: [] },
      finishOrder: [],
      playedByRank: new Array(13).fill(0),
      history: [],
    },
    exchange: null,
    lastFinishOrder: null,
    finishOrders: [],
    lastDeal: null,
  };
}

/* ------------------------------------------------------------------ *
 * El sonu kart değişimi
 * ------------------------------------------------------------------ */

function buildExchange(match: Match, order: Seat[]): Exchange {
  const deal = match.deal!;
  const { exchangeFirstLast: nFL, exchangeSecondThird: nST } = match.config;
  const [first, second, third, last] = order as [Seat, Seat, Seat, Seat];

  const forced: Exchange['forced'] = [];
  const pending: Exchange['pending'] = [];

  // Kaybeden tarafın verdiği kartlar ZORUNLU: elindeki en yüksekler.
  const pairs: [Seat, Seat, number][] = [
    [last, first, nFL],
    [third, second, nST],
  ];
  for (const [from, to, n] of pairs) {
    if (n <= 0) continue;
    const cards = highestCards(deal.hands[from]!, n);
    deal.hands[from] = sortHand(deal.hands[from]!.filter((c) => !cards.includes(c)));
    deal.hands[to] = sortHand([...deal.hands[to]!, ...cards]);
    forced.push({ from, to, cards });
    // Kazanan taraf karşılığında SERBEST seçim yapar — az önce aldığı kartlar
    // da dahil, elindeki her şeyden seçebilir.
    pending.push({ from: to, to: from, count: n });
  }

  return { forced, pending, chosen: [] };
}

/** Serbest yönde verilecek kartları bildirir. İki taraf da bildirince el başlar. */
export function submitExchange(match: Match, seat: Seat, cards: CardId[]): Match {
  if (match.phase !== 'exchange' || !match.exchange || !match.deal) {
    throw new IllegalMoveError('Şu anda kart değişimi yapılmıyor');
  }
  const ex = match.exchange;
  const pending = ex.pending.find((p) => p.from === seat);
  if (!pending) throw new IllegalMoveError('Senden kart beklenmiyor');
  if (ex.chosen.some((c) => c.from === seat)) {
    throw new IllegalMoveError('Kartlarını zaten verdin');
  }
  if (cards.length !== pending.count) {
    throw new IllegalMoveError(`${pending.count} kart vermelisin`);
  }
  if (new Set(cards).size !== cards.length) {
    throw new IllegalMoveError('Aynı kart birden fazla kez verilemez');
  }
  const hand = new Set(match.deal.hands[seat]!);
  if (!cards.every((c) => hand.has(c))) throw new IllegalMoveError('Bu kartlar elinde yok');

  const next = clone(match);
  next.exchange!.chosen.push({ from: seat, to: pending.to, cards: [...cards] });

  if (next.exchange!.chosen.length === next.exchange!.pending.length) {
    resolveExchange(next);
  }
  return next;
}

/** Timeout / bot: serbest yönde en düşük kartları verir. */
export function autoExchange(match: Match, seat: Seat): Match {
  const pending = match.exchange?.pending.find((p) => p.from === seat);
  if (!pending) throw new IllegalMoveError('Senden kart beklenmiyor');
  return submitExchange(match, seat, lowestCards(match.deal!.hands[seat]!, pending.count));
}

function resolveExchange(match: Match): void {
  const deal = match.deal!;
  for (const { from, to, cards } of match.exchange!.chosen) {
    const given = new Set(cards);
    deal.hands[from] = sortHand(deal.hands[from]!.filter((c) => !given.has(c)));
    deal.hands[to] = sortHand([...deal.hands[to]!, ...cards]);
  }
  match.phase = 'play';
}

/* ------------------------------------------------------------------ *
 * Timeout davranışı
 * ------------------------------------------------------------------ */

/**
 * Süresi dolan oyuncunun otomatik hamlesi.
 * Takipçi konumunda pas geçer; LİDER pas geçemeyeceği için en düşük tekli kartı oynar.
 */
export function timeoutMove(match: Match, seat: Seat): Move {
  const deal = match.deal!;
  if (deal.trick.count === 0) {
    return { type: 'play', cards: lowestCards(deal.hands[seat]!, 1) };
  }
  return { type: 'pass' };
}

/* ------------------------------------------------------------------ *
 * Skor
 * ------------------------------------------------------------------ */

export const currentSet = (match: Match): number =>
  Math.floor(match.dealIndex / match.config.dealsPerSet) + 1;

/** Maç sonu sıralaması. Eşit puanlılar aynı `place` değerini alır (ortak birincilik). */
export function standings(match: Match): { seat: Seat; score: number; place: number }[] {
  const rows = SEATS.map((seat) => ({ seat, score: match.scores[seat]!, place: 0 }));
  rows.sort((a, b) => b.score - a.score);
  rows.forEach((r, i) => {
    r.place = i > 0 && rows[i - 1]!.score === r.score ? rows[i - 1]!.place : i + 1;
  });
  return rows;
}
